import { useEffect, useRef, useState, useCallback } from "react";
import { heroSlides } from "@/mocks/portfolio";
import FadeIn from "@/components/base/FadeIn";

function TiltCard({ slide, index, total }: { slide: (typeof heroSlides)[0]; index: number; total: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0, glowX: 50, glowY: 50 });
  const [isHovering, setIsHovering] = useState(false);
  const rafRef = useRef<number>(0);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const maxTilt = 18;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      setTilt({
        rotateX: (y - 0.5) * -maxTilt,
        rotateY: (x - 0.5) * maxTilt,
        glowX: x * 100,
        glowY: y * 100,
      });
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setIsHovering(false);
    setTilt({ rotateX: 0, rotateY: 0, glowX: 50, glowY: 50 });
  }, []);

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  const sizeClass = (size?: string) => {
    switch (size) {
      case "tall":
        return {
          wrap: "w-[280px] md:w-[320px] lg:w-[360px]",
          box: "h-[420px] md:h-[500px] lg:h-[560px]",
          offset: "self-start",
        };
      case "short":
        return {
          wrap: "w-[260px] md:w-[300px] lg:w-[340px]",
          box: "h-[320px] md:h-[380px] lg:h-[420px]",
          offset: "self-end mb-12 md:mb-20",
        };
      case "wide":
        return {
          wrap: "w-[400px] md:w-[460px] lg:w-[520px]",
          box: "h-[300px] md:h-[340px] lg:h-[380px]",
          offset: "self-center",
        };
      default:
        return {
          wrap: "w-[280px] md:w-[320px] lg:w-[360px]",
          box: "h-[420px] md:h-[500px] lg:h-[560px]",
          offset: "self-start",
        };
    }
  };

  const s = sizeClass(slide.size);

  return (
    <div
      className={`shrink-0 ${s.wrap} ${s.offset} fade-up group/tilt`}
      style={{ animationDelay: `${index * 80}ms`, perspective: "1000px" }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        ref={cardRef}
        className={`relative w-full ${s.box} overflow-hidden cursor-pointer transition-transform duration-[600ms] ease-out`}
        style={{
          transform: isHovering
            ? `rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) scale(1.03)`
            : "rotateX(0deg) rotateY(0deg) scale(1)",
          transformStyle: "preserve-3d",
        }}
      >
        <img
          src={slide.image}
          alt={slide.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
          style={{
            transform: isHovering
              ? `translateZ(30px) translateX(${(tilt.glowX - 50) * 0.08}px) translateY(${(tilt.glowY - 50) * 0.08}px)`
              : "translateZ(0px)",
            transition: "transform 600ms ease-out",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/40 via-transparent to-transparent pointer-events-none"></div>

        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-500"
          style={{ opacity: isHovering ? 1 : 0 }}
        >
          <div
            className="absolute w-[200px] h-[200px] rounded-full -translate-x-1/2 -translate-y-1/2 blur-[60px]"
            style={{
              left: `${tilt.glowX}%`,
              top: `${tilt.glowY}%`,
              background: "radial-gradient(circle, oklch(var(--accent-500) / 0.22), transparent 70%)",
              transition: "left 0.15s ease-out, top 0.15s ease-out",
            }}
          />
        </div>

        {/* Other overlays... */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute top-0 w-[30%] h-full bg-gradient-to-r from-transparent via-accent-500/8 to-transparent animate-light-sweep"
            style={{ animationDelay: `${index * 0.6}s` }}
          />
        </div>

        {/* Hover corner decorations */}
        <div
          className="absolute inset-0 pointer-events-none transition-opacity duration-500"
          style={{ opacity: isHovering ? 1 : 0 }}
        >
          <span className="absolute top-3 left-3 w-6 h-6 border-t border-l border-accent-400/60" />
          <span className="absolute top-3 right-3 w-6 h-6 border-t border-r border-accent-400/60" />
          <span className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-accent-400/60" />
          <span className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-accent-400/60" />
        </div>

        {/* Roman-style painting frame */}
        <div className="absolute inset-0 z-10 pointer-events-none">
          <div className="absolute inset-0 border-[4px] border-accent-500/50" />
          <div className="absolute inset-[4px] border-[2px] border-accent-500/25" />
          <div className="absolute top-[4px] left-[4px] w-5 h-5 border-t-[3px] border-l-[3px] border-accent-500" />
          <div className="absolute top-[4px] right-[4px] w-5 h-5 border-t-[3px] border-r-[3px] border-accent-500" />
          <div className="absolute bottom-[4px] left-[4px] w-5 h-5 border-b-[3px] border-l-[3px] border-accent-500" />
          <div className="absolute bottom-[4px] right-[4px] w-5 h-5 border-b-[3px] border-r-[3px] border-accent-500" />
        </div>
      </div>

      <div className="mt-4 flex items-baseline justify-between gap-3">
        <span className="font-sans text-[10px] uppercase tracking-[0.22em] text-background-50/55 tabular-nums">
          {String((index % total) + 1).padStart(2, "0")}
        </span>
        <span className="font-serif italic text-background-50/85 text-sm md:text-base text-right truncate">
          {slide.caption}
        </span>
      </div>
    </div>
  );
}

export default function Statement() {
  const total = heroSlides.length;

  const words = [
    "SACRED",
    "·",
    "PERMANENCE",
    "·",
    "TRADITION",
    "·",
    "VISION",
    "·",
    "LEGACY",
    "·",
  ];

  const sectionRef = useRef<HTMLElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hoveredCircle, setHoveredCircle] = useState<number | null>(null);

  // Carousel state
  const [displayIndex, setDisplayIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const autoRef = useRef<number>(0);
  const oneSetWidth = useRef<number>(0);
  const isJumping = useRef(false);
  const tripledSlides = [...heroSlides, ...heroSlides, ...heroSlides];

  const headlineParts: { text: string; muted?: boolean; italic?: boolean }[] = [
    { text: "WITH REVERENCE,", muted: true },
    { text: "WE" },
    { text: "ARE" },
    { text: "BUILDING" },
    { text: "A" },
    { text: "SANCTUARY" },
    { text: "WHERE" },
    { text: "art", italic: true },
    { text: "AND" },
    { text: "flesh", italic: true },
    { text: ",", muted: true },
    { text: "MEET" },
    { text: "TRADITION" },
    { text: "AND" },
    { text: "VISION," },
    { text: "HONORING" },
    { text: "THE" },
    { text: "ANCIENT" },
    { text: "AND" },
    { text: "CREATING" },
    { text: "THE" },
    { text: "ETERNAL.", },
  ];

  useEffect(() => {
    if (!sectionRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setInView(true);
        });
      },
      { threshold: 0.2 }
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const totalH = rect.height + vh;
      const passed = vh - rect.top;
      const p = Math.max(0, Math.min(1, passed / totalH));
      setProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Initialize carousel scroll position to middle (second) copy
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const raf = requestAnimationFrame(() => {
      oneSetWidth.current = track.scrollWidth / 3;
      track.scrollLeft = oneSetWidth.current;
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Normalize display index from carousel scroll position
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (isJumping.current) return;
        const children = Array.from(track.children) as HTMLElement[];
        if (!children.length) return;
        const trackLeft = track.scrollLeft + 24;
        let closest = 0;
        let closestDist = Infinity;
        children.forEach((el, i) => {
          const d = Math.abs(el.offsetLeft - trackLeft);
          if (d < closestDist) {
            closestDist = d;
            closest = i;
          }
        });
        setDisplayIndex(closest % total);
      });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      track.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [total]);

  // Infinite auto-scroll for carousel
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let last = performance.now();
    const speed = 30;

    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      if (track && oneSetWidth.current > 0) {
        track.scrollLeft += speed * dt;

        if (track.scrollLeft >= oneSetWidth.current * 2 - 1) {
          isJumping.current = true;
          track.scrollLeft -= oneSetWidth.current;
          isJumping.current = false;
        }
      }
      autoRef.current = requestAnimationFrame(tick);
    };

    autoRef.current = requestAnimationFrame(tick);
    return () => {
      if (autoRef.current) cancelAnimationFrame(autoRef.current);
    };
  }, []);

  const next = () => {
    const track = trackRef.current;
    if (!track) return;
    const children = Array.from(track.children) as HTMLElement[];
    if (!children.length) return;
    const targetIndex = displayIndex + 1;
    const card = children[total + (targetIndex % total)] as HTMLElement | undefined;
    if (!card) return;
    const left = card.offsetLeft - 24;
    track.scrollTo({ left, behavior: "smooth" });
  };

  const prev = () => {
    const track = trackRef.current;
    if (!track) return;
    const children = Array.from(track.children) as HTMLElement[];
    if (!children.length) return;
    const normalized = ((displayIndex - 1) % total + total) % total;
    const card = children[total + normalized] as HTMLElement | undefined;
    if (!card) return;
    const left = card.offsetLeft - 24;
    track.scrollTo({ left, behavior: "smooth" });
  };

  const parallaxOffset = (i: number) => {
    const base = (progress - 0.5) * 80;
    return i % 2 === 0 ? -base : base;
  };

  const marqueeShift = (progress - 0.5) * 200;

  const circles = [
    "https://readdy.ai/api/search-image?query=Luxury%20tattoo%20studio%20marble%20counter%20with%20classical%20Roman%20bust%20sculpture%20and%20warm%20amber%20museum%20lighting%20rich%20terracotta%20and%20bronze%20tones%20renaissance%20gallery%20atmosphere%20soft%20golden%20glow%20ivory%20marble%20surface%20editorial%20interior%20photography%20deep%20warm%20shadows&width=500&height=500&seq=stmt-circ-t001&orientation=squarish",
    "https://readdy.ai/api/search-image?query=Close%20up%20of%20fine%20art%20tattoo%20needle%20and%20gold%20pigment%20on%20ivory%20marble%20surface%20classical%20painting%20tools%20aesthetic%20warm%20bronze%20lighting%20renaissance%20atelier%20atmosphere%20editorial%20product%20photography%20rich%20terracotta%20accents&width=500&height=500&seq=stmt-circ-t002&orientation=squarish",
    "https://readdy.ai/api/search-image?query=Ancient%20Egyptian%20hieroglyphic%20stone%20carving%20detail%20with%20warm%20golden%20amber%20side%20lighting%20classical%20archaeological%20texture%20deep%20terracotta%20and%20bronze%20tones%20museum%20gallery%20aesthetic%20rich%20historical%20atmosphere&width=500&height=500&seq=stmt-circ-t003&orientation=squarish",
    "https://readdy.ai/api/search-image?query=Renaissance%20artist%20hand%20sketching%20classical%20design%20on%20parchment%20paper%20with%20warm%20chiaroscuro%20lighting%20baroque%20atelier%20atmosphere%20rich%20bronze%20and%20terracotta%20tones%20vintage%20art%20studio%20aesthetic%20editorial%20photography&width=500&height=500&seq=stmt-circ-t004&orientation=squarish",
  ];

  return (
    <section
      ref={sectionRef}
      className="relative bg-foreground-950 text-background-50 py-24 md:py-36 overflow-hidden"
      id="about"
    >
      {/* Ambient glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full opacity-[0.04] pointer-events-none"
        style={{ background: "radial-gradient(ellipse, oklch(var(--accent-500)), transparent 70%)" }} />

      {/* Marquee */}
      <div className="overflow-hidden mb-16 md:mb-24">
        <div
          ref={marqueeRef}
          className="flex gap-12 animate-marquee whitespace-nowrap will-change-transform"
          style={{
            transform: `translateX(${marqueeShift}px)`,
            transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          {[...words, ...words, ...words, ...words].map((w, i) => (
            <span
              key={i}
              className="font-display font-black text-5xl md:text-7xl tracking-tight text-background-50/90"
            >
              {w}
            </span>
          ))}
        </div>
      </div>

      {/* Headline */}
      <div className="px-6 md:px-12 max-w-6xl mx-auto">
        <p className="font-display font-medium text-2xl md:text-5xl leading-[1.15] tracking-tight uppercase text-background-50">
          {headlineParts.map((part, i) => {
            const delay = i * 60;
            const isPunct = part.text === "," || part.text === ".";
            return (
              <span
                key={i}
                className={`inline-block overflow-hidden align-baseline ${
                  isPunct ? "mr-0" : "mr-[0.22em]"
                }`}
              >
                <span
                  className={`inline-block transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
                    inView
                      ? "translate-y-0 opacity-100"
                      : "translate-y-[120%] opacity-0"
                  } ${part.muted ? "text-background-50/40" : ""} ${
                    part.italic ? "font-serif font-light italic" : ""
                  }`}
                  style={{ transitionDelay: `${delay}ms` }}
                >
                  {part.text}
                </span>
              </span>
            );
          })}
        </p>
      </div>

      {/* Circular images */}
      <div className="px-6 md:px-12 mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 max-w-6xl mx-auto">
        {circles.map((src, i) => (
          <FadeIn key={i} delay={i * 100} className="relative cursor-pointer">
            <div
              onMouseEnter={() => setHoveredCircle(i)}
              onMouseLeave={() => setHoveredCircle(null)}
              style={{
                transform: `translateY(${parallaxOffset(i)}px)`,
                transition: "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1), filter 0.5s ease",
                filter:
                  hoveredCircle !== null && hoveredCircle !== i
                    ? "grayscale(1) brightness(0.55)"
                    : "grayscale(0)",
              }}
            >
              <div
                className={`absolute inset-[-10px] rounded-full border border-dashed border-accent-500/30 transition-all duration-700 ${
                  hoveredCircle === i
                    ? "opacity-100 scale-100 animate-spin-slow"
                    : "opacity-0 scale-90"
                }`}
              />
              <div className="img-hover aspect-square rounded-full overflow-hidden relative">
                <img
                  src={src}
                  alt={`Detail ${i + 1}`}
                  className={`w-full h-full object-cover object-top transition-transform duration-700 ${
                    hoveredCircle === i ? "scale-110" : "scale-100"
                  }`}
                />
                <div
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-500 ${
                    hoveredCircle === i ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <span className="font-display text-xs tracking-[0.3em] text-background-50 bg-foreground-950/60 px-3 py-1 rounded-full">
                    0{i + 1}
                  </span>
                </div>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>

      {/* ── Gallery Carousel ── */}
      <div className="mt-20 md:mt-28">
        {/* Carousel header row */}
        <div className="px-6 md:px-12 max-w-6xl mx-auto flex items-end justify-between mb-8 md:mb-10">
          <div>
            <span className="font-sans text-xs uppercase tracking-[0.22em] text-background-50/45">
              Gallery
            </span>
          </div>
          <div className="flex items-center gap-5">
            <span className="font-sans text-sm tracking-wider text-background-50/70 tabular-nums">
              {String(displayIndex + 1).padStart(2, "0")}
              <span className="text-background-50/35"> / {String(total).padStart(2, "0")}</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={prev}
                aria-label="Previous"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-background-50/25 text-background-50 hover:bg-background-50 hover:text-foreground-950 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-left-line text-base"></i>
              </button>
              <button
                onClick={next}
                aria-label="Next"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-background-50/25 text-background-50 hover:bg-background-50 hover:text-foreground-950 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-arrow-right-line text-base"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Horizontal gallery strip */}
        <div
          ref={trackRef}
          className="flex items-stretch gap-4 md:gap-6 overflow-x-auto hero-track px-6 md:px-12 pb-2"
          style={{ scrollbarWidth: "none" }}
        >
          {tripledSlides.map((slide, i) => (
            <TiltCard key={`${slide.id}-${i}`} slide={slide} index={i} total={total} />
          ))}
        </div>
      </div>

      <style>{`
        .hero-track::-webkit-scrollbar { display: none; }
      `}</style>

      {/* Bottom hint */}
      <div
        className={`px-6 md:px-12 mt-16 md:mt-24 max-w-6xl mx-auto flex items-center justify-between text-background-50/40 font-display text-[11px] uppercase tracking-[0.3em] transition-opacity duration-1000 ${
          inView ? "opacity-100" : "opacity-0"
        }`}
        style={{ transitionDelay: "1400ms" }}
      >
        <span>— About Fine Arts Tattoo Collective</span>
        <span className="hidden md:inline">Scroll to continue ↓</span>
      </div>
    </section>
  );
}